import { InjectionToken } from '@angular/core';
import {
  VmCallPreviewRequest,
  VmPatient,
  VmSettings,
  VmTranscript,
  Voicemail,
} from '@models';

/** Provider-authorized voicemail operations. Implemented against the API, and by a demo double. */
export abstract class VoicemailGateway {
  /** Drop provider-specific cached state before a different session is loaded. */
  abstract reset(): void;
  abstract list(): Promise<Voicemail[]>;
  /**
   * One voicemail's transcript. Its own call because reading it is an audited access to patient
   * content: the inbox carries the status so the list can be rendered, and the words only travel
   * when somebody actually opens the message.
   */
  abstract getTranscript(id: string): Promise<VmTranscript>;

  abstract getSettings(): Promise<VmSettings>;
  /**
   * Ask the carrier what happened to an activation whose outcome was never seen, and finish it if a
   * number was bought. Separate from {@link getSettings} because it can bind a number and put the
   * line live: reading a screen must not do that, and only the provider pressing "Check status"
   * should.
   */
  abstract reconcile(): Promise<VmSettings>;
  abstract saveSettings(settings: VmSettings): Promise<VmSettings>;
  /**
   * Choose whether the website shows the voicemail number or the practice phone. Apart from
   * {@link saveSettings} so flipping the switch never carries an unsaved greeting with it.
   */
  abstract setWebsiteNumber(useVoicemailNumber: boolean): Promise<VmSettings>;
  abstract activate(): Promise<VmSettings>;
  /**
   * Resolve a recordingRef to playable audio. Returns a blob rather than a URL so the request
   * carries the caller's credentials and can be refused; a bare URL on an <audio> element would
   * send none.
   */
  abstract getAudio(recordingRef: string): Promise<Blob>;
  /**
   * What a caller would hear before the tone, as audio, spoken by the same voice and from the same
   * script rules as a real call. Not saved settings: the greeting and link as they are on screen.
   */
  abstract getPreviewAudio(request: VmCallPreviewRequest): Promise<Blob>;
  abstract markUnread(id: string, unread: boolean): Promise<Voicemail>;
  abstract searchPatients(query: string): Promise<VmPatient[]>;
  abstract linkPatient(
    id: string,
    patientId: string | null
  ): Promise<Voicemail>;
}

/**
 * A gateway failure that kept the API's own error code.
 *
 * The code is what tells a definite refusal ("publish your landing page first") apart from an
 * outcome nobody knows yet. Flattened to a message, every activation failure looked equally
 * uncertain, and the portal parked the line in "checking" — a state whose only exit is asking the
 * carrier about a purchase that never happened.
 */
export class VoicemailGatewayError extends Error {
  constructor(
    message: string,
    readonly code: string | null
  ) {
    super(message);
    this.name = 'VoicemailGatewayError';
  }
}

export const VOICEMAIL_GATEWAY = new InjectionToken<VoicemailGateway>(
  'VOICEMAIL_GATEWAY'
);
